<script setup lang="ts">
import { ref } from "vue"
import { useI18n } from "vue-i18n"
import type { Client, CreatePrefill } from "@/entities/client"
import { clientApi } from "@/entities/client"
import { toast } from "@/shared/lib"
import { normalizeError } from "@/shared/api"
import { Button, FormAlert, Input, Label } from "@/shared/ui"

defineOptions({ name: "InlineClientCreate" })

const props = defineProps<{
  prefill: CreatePrefill
}>()

const emit = defineEmits<{
  created: [Client]
  back: []
}>()

const { t } = useI18n()

const firstName = ref(props.prefill.firstName ?? "")
const lastName = ref("")
const phone = ref(props.prefill.phone ?? "")
const saving = ref(false)
const phoneError = ref<string | null>(null)

async function handleSubmit(): Promise<void> {
  if (!firstName.value.trim() || !phone.value.trim()) return
  phoneError.value = null
  saving.value = true
  try {
    const client = await clientApi.createClient({
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      phone: phone.value.trim(),
      email: "",
    })
    emit("created", client)
  } catch (err) {
    const appError = normalizeError(err)
    // B1: client_phone_exists = conflict (409), не 422
    if (appError.kind === "conflict" && appError.code === "client_phone_exists") {
      phoneError.value = t("records.createFlow.inlineCreate.phoneExists")
    } else {
      toast.error(t("records.createFlow.inlineCreate.createError"))
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="inline-create" data-testid="inline-client-create">
    <p class="inline-create__title">{{ t("records.createFlow.inlineCreate.title") }}</p>

    <div class="inline-create__fields">
      <div class="inline-create__field">
        <Label for="inline-first-name">{{ t("records.createFlow.inlineCreate.firstName") }}</Label>
        <Input
          id="inline-first-name"
          v-model="firstName"
          autocomplete="given-name"
          :placeholder="t('records.createFlow.inlineCreate.firstName')"
        />
      </div>

      <div class="inline-create__field">
        <Label for="inline-phone">
          {{ t("records.createFlow.inlineCreate.phone") }}
        </Label>
        <Input
          id="inline-phone"
          v-model="phone"
          type="tel"
          inputmode="tel"
          autocomplete="tel"
        />
        <FormAlert
          v-if="phoneError"
          data-testid="inline-phone-error"
          variant="destructive"
          :message="phoneError"
        />
      </div>

      <div class="inline-create__field">
        <Label for="inline-last-name">
          {{ t("records.createFlow.inlineCreate.lastName") }}
          <span class="inline-create__optional">{{ t("records.createFlow.inlineCreate.optional") }}</span>
        </Label>
        <Input
          id="inline-last-name"
          v-model="lastName"
          autocomplete="family-name"
        />
      </div>
    </div>

    <div class="inline-create__actions">
      <Button
        type="button"
        class="w-full"
        data-testid="btn-inline-create"
        :loading="saving"
        :disabled="!firstName.trim() || !phone.trim()"
        @click="handleSubmit"
      >
        {{ t("records.createFlow.inlineCreate.submit") }}
      </Button>
      <Button
        type="button"
        variant="ghost"
        class="w-full"
        @click="emit('back')"
      >
        {{ t("records.createFlow.inlineCreate.back") }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.inline-create {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.inline-create__title {
  font-size: 0.9375rem;
  font-weight: 600;
}

.inline-create__fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.inline-create__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.inline-create__optional {
  font-size: 0.75rem;
  color: var(--muted-foreground);
  font-weight: 400;
  margin-left: 4px;
}

.inline-create__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
